import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { forkJoin, of } from "rxjs";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import {
  AccordionTableViewComponent
} from "../../common/accordion-table-view/accordion-table-view.component";
import { MessageEnum } from "../../../core/utils/message.enum";

@Component({
  selector: "app-test-plan-modal",
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, AccordionTableViewComponent],
  templateUrl: "./test-plan-modal.component.html",
  styleUrl: "./test-plan-modal.component.scss",
})
export class TestPlanModalComponent implements OnInit {
  activeModal = inject(NgbActiveModal);
  dynamicForm: any = new FormGroup({
    testPlanName: new FormControl(""),
  });

  isActive: boolean = false;
  isLoading: boolean = false;
  isCreating: boolean = false;
  selectedTestCaseCount: number = 0;

  // Global "Set as Default" state from accordion header
  isDefaultEnabled: boolean = false;
  globalAgentId: string | null = null;
  globalBrowser: string | null = null;

  agentMap: Record<string, string> = {};
  deviceMap: Record<string, string> = {};
  executionEngineOptions: any[] = [];
  executionEngineTableOptions: any[] = [];

  browserOptions: any[] = [
    {
      label: "Chrome",
      value: "Chrome",
      image: "assets/images/virtusa-icons/chrome.svg",
    },
    {
      label: "Firefox",
      value: "Firefox",
      image: "assets/images/virtusa-icons/firefox.svg",
    },
    {
      label: "Edge",
      value: "Edge",
      image: "assets/images/virtusa-icons/edge.svg",
    },
  ];

  @Input() configs: any = {};
  @Input() planId: string = "";
  selectedTestCases: any = [];

  constructor(public restService: RestService, private readonly toaster: ToasterService) { }

  ngOnInit() {
    this.getTestPlansAndTestSuits();
  }

  initializeTableRows() {
    // Initialize select property for all rows
    this.configs.accordionData.forEach((suite: any) => {
      suite.tableRows.forEach((row: any) => {
        row.select = false;
      });
    });
  }

  onTableRowSelect(event: any, section: any) {
    if (event && event.data !== undefined) {
      const sectionIds = new Set(
        (section.tableRows ?? []).map((r: any) => r.testCaseId),
      );

      // Remove every entry from this section
      this.selectedTestCases = this.selectedTestCases.filter(
        (tc: any) => !sectionIds.has(tc.testCaseId),
      );

      // event.data already contains only rows with select === true
      event.data.forEach((row: any) => {
        this.selectedTestCases.push({
          testCaseId: row.testCaseId,
          scriptId: row.scriptId,
          testCaseName: row.testCaseName,
          version: row.version,
          increment: row.increment ?? 0,
          tcgTestCaseResultId: row.tcgTestCaseResultId ?? null,
          executionEngine: row.executionEngine,
          browser: row.browser,
        });
      });

      this.selectedTestCaseCount = this.selectedTestCases.length;
    }
  }

  onSelectAllChange(checked: boolean): void {
    if (checked) {
      this.selectedTestCases = [];
      this.configs.accordionData.forEach((suite: any) => {
        suite.tableRows?.forEach((row: any) => {
          this.selectedTestCases.push({
            testCaseId: row.testCaseId,
            scriptId: row.scriptId,
            testCaseName: row.testCaseName,
            version: row.version,
            increment: row.increment ?? 0,
            tcgTestCaseResultId: row.tcgTestCaseResultId ?? null,
            executionEngine: row.executionEngine,
            browser: row.browser,
          });
        });
      });
    } else {
      this.selectedTestCases = [];
      this.configs.accordionData.forEach((suite: any) => {
        suite.tableRows?.forEach((row: any) => (row.select = false));
      });
    }
    this.selectedTestCaseCount = this.selectedTestCases.length;
  }

  handleTableActions(event: any, section: any) {
    const { type, data, value } = event ?? {};
    const row = data?.row;
    const key = data?.column?.key;
    
    if (!row) return;

    if (this.isExecutionEngineAction(type, key)) {
      this.handleExecutionEngineUpdate(row, value);
    } else if (this.isBrowserAction(type, key)) {
      this.handleBrowserUpdate(row, value);
    } else if (this.isVersionAction(type, key)) {
      this.handleVersionUpdate(row, value);
    }
  }

  private isExecutionEngineAction(type: string, key: string): boolean {
    const validTypes = ['multi-select', 'section_dropdown', 'menu_dropdown'];
    return validTypes.includes(type) && key === 'executionEngine';
  }

  private isBrowserAction(type: string, key: string): boolean {
    return type === 'browser-select' && key === 'browser';
  }

  private isVersionAction(type: string, key: string): boolean {
    return type === 'multi-select' && key === 'version';
  }

  private handleExecutionEngineUpdate(row: any, value: any): void {
    const resolved = this.resolveExecutionEngineValue(row, value);
    row.executionEngine = resolved;
    this.syncSelectedTestCase(row.testCaseId, 'executionEngine', resolved);
  }

  private handleBrowserUpdate(row: any, value: any): void {
    row.browser = value ?? row.browser ?? null;
    this.syncSelectedTestCase(row.testCaseId, 'browser', row.browser);
  }

  private handleVersionUpdate(row: any, value: any): void {
    const versionValue = row.version ?? value ?? null;
    this.syncSelectedTestCase(row.testCaseId, 'version', versionValue);
  }

  private resolveExecutionEngineValue(row: any, value: any): any {
    return value?.value ?? row.executionEngine ?? value ?? null;
  }

  private syncSelectedTestCase(testCaseId: string, property: string, value: any): void {
    const selectedEntry = this.selectedTestCases.find(
      (tc: any) => tc.testCaseId === testCaseId
    );
    if (selectedEntry) {
      selectedEntry[property] = value;
    }
  }

  initializeDropdownOptions() {
    this.configs.accordionData.forEach((suite: any) => {
      const versionCol = suite.tableColumns.find(
        (col: any) => col.key === "version",
      );
      const engineCol = suite.tableColumns.find(
        (col: any) => col.key === "executionEngine",
      );
      const browserCol = suite.tableColumns.find(
        (col: any) => col.key === "browser",
      );

      if (engineCol) {
        if (engineCol._config.type === 'menu_dropdown') {
          engineCol._config.componentInputs.fields = this.executionEngineTableOptions;
        } else {
          engineCol._config.componentInputs.fields = this.executionEngineOptions;
        }
      }

      // Version and browser options are per-row
      suite.tableRows.forEach((row: any) => {
        if (versionCol && row.versionOptions) {
          versionCol._config.options = row.versionOptions.map(
            (opt: string) => ({ label: opt, value: opt }),
          );
        }
        if (browserCol && row.browserOptions) {
          browserCol._config.options = row.browserOptions.map(
            (opt: string) => ({ label: opt, value: opt }),
          );
        }
      });
    });
  }

  removeTestCase(testCase: any): void {
    const index = this.selectedTestCases.findIndex(
      (tc: any) => tc.testCaseId === testCase.testCaseId,
    );
    if (index > -1) {
      this.selectedTestCases.splice(index, 1);
      this.selectedTestCaseCount = this.selectedTestCases.length;

      // Uncheck the row in the table and replace the array reference so
      // ZorroTable's ngOnChanges fires and re-syncs the checkbox state
      this.configs.accordionData.forEach((suite: any) => {
        const rowIndex = suite.tableRows.findIndex(
          (r: any) => r.testCaseId === testCase.testCaseId,
        );
        if (rowIndex > -1) {
          suite.tableRows[rowIndex].select = false;
          suite.tableRows = [...suite.tableRows];
        }
      });
    }
  }

  onSwitchToggle(enabled: boolean): void {
    this.isDefaultEnabled = enabled;
    this.applyDefaultsToRows();
  }

  onExecutionEngineChange(value: any): void {
    this.globalAgentId = value?.id || value?.value || value || null;
    this.applyDefaultsToRows();
  }

  onBrowserChange(value: any): void {
    this.globalBrowser = value?.id || value?.value || value || null;
    this.applyDefaultsToRows();
  }

  private applyDefaultsToRows(): void {
    if (!this.isDefaultEnabled) return;

    // Build a set of selected testCaseIds for fast lookup
    const selectedIds = new Set(this.selectedTestCases.map((tc: any) => tc.testCaseId));

    this.configs.accordionData.forEach((suite: any) => {
      let changed = false;
      suite.tableRows.forEach((row: any) => {
        if (!selectedIds.has(row.testCaseId)) return;
        if (this.globalAgentId !== null) row.executionEngine = this.globalAgentId;
        if (this.globalBrowser !== null) row.browser = this.globalBrowser;
        changed = true;
      });
      // Only trigger ZorroTable ngOnChanges if something actually changed
      if (changed) suite.tableRows = [...suite.tableRows];
    });

    // Sync selectedTestCases array too
    this.selectedTestCases.forEach((tc: any) => {
      if (this.globalAgentId !== null) tc.executionEngine = this.globalAgentId;
      if (this.globalBrowser !== null) tc.browser = this.globalBrowser;
    });
  }

  popErrorToaster(message: string, showDefaultError?: boolean) {
    this.toaster.pop({ type: 'error', text: showDefaultError ? MessageEnum.SOMETHING_WENT_WRONG : message });
  }

  create() {

    let allSelected = this.configs.accordionData.length === this.selectedTestCaseCount;

    if (allSelected && this.isDefaultEnabled && !this.globalBrowser && !this.globalAgentId) {
      this.toaster.pop({ type: 'warning', text: MessageEnum.SELECT_BROWSER_AND_EXECUTION_ENGINE_BEFORE_SAVING });
      return;
    }

    const toAgentId = (val: string | null | undefined) =>
      val === "onDemand" ? null : (val ?? null);

    const testScripts = this.selectedTestCases.map((tc: any, index: number) => {
      const agentId = this.isDefaultEnabled
        ? toAgentId(this.globalAgentId ?? tc.executionEngine)
        : toAgentId(tc.executionEngine);

      const browser = this.isDefaultEnabled
        ? (this.globalBrowser ?? tc.browser ?? null)
        : (tc.browser ?? null);
      return {
        sequence: index,
        id: tc.testCaseId,
        scriptId: tc.scriptId,
        tcgTestCaseResultId: tc.tcgTestCaseResultId ?? null,
        name: tc.testCaseName,
        increment: tc.increment ?? 0,
        agentId,
        browser,
        version: tc.version ?? null,
      };
    });

    if (!allSelected || (allSelected && !this.isDefaultEnabled)) {
      const missingBrowser = testScripts.some((ts: any) => !ts.browser);
      if (missingBrowser) {
        this.toaster.pop({ type: 'warning', text: MessageEnum.SELECT_BROWSER_FOR_ALL_TEST_CASES });
        return;
      }
    }

    const payload = {
      name: this.dynamicForm.get("testPlanName")?.value,
      testScripts,
    };

    this.isCreating = true;
    const request$ = this.planId
      ? this.restService.EditTestPlanById(
        this.configs?.projectId,
        this.configs?.automationProjectId,
        this.planId,
        payload,
      )
      : this.restService.addNewTestPlan(
        this.configs?.projectId,
        this.configs?.automationProjectId,
        payload,
      );

    request$.subscribe({
      next: () => {
        this.isCreating = false;
        this.activeModal.close({
          testPlanName: this.dynamicForm.get("testPlanName")?.value,
        });
      },
      error: (errMessage: any) => {
        this.isCreating = false;
        const message = errMessage || 'An error occurred while saving the test plan.';
        this.popErrorToaster(message);
      },
    });
  }

  getTestPlansAndTestSuits() {
    this.isLoading = true;

    forkJoin({
      scripts: this.restService.getTestSuitsAndTestCases(
        this.configs?.projectId,
        this.configs?.automationProjectId,
      ),
      agents: this.restService.getAgentsWithStatus(this.configs?.projectId),
      devices: this.restService.getDevices(this.configs?.projectId),
      plan: this.planId
        ? this.restService.getTestPlanById(
          this.configs?.projectId,
          this.configs?.automationProjectId,
          this.planId,
        )
        : of(null),
    }).subscribe({
      next: ({ scripts, agents, devices, plan }) => {
        // Process agents and devices
        const localAgents: any[] = [];
        const cloudAgents: any[] = [];
        const localDevices: any[] = [];
        const remoteDevices: any[] = [];

        // Process agents
        (agents?.data ?? [])
          .filter((a: any) => a.active)
          .forEach((agent: any) => {
            const opt = {
              label: agent.name,
              icon:
                agent.status === "OFFLINE"
                  ? "assets/images/agent/agent-dead.svg"
                  : "assets/images/agent/agent-alive.svg",
              value: agent.id,
              agentType: agent.type,
            };
            // Populate agentMap for name resolution
            this.agentMap[agent.id] = agent.name;
            if (agent.type === "LOCAL") {
              localAgents.push(opt);
            } else {
              cloudAgents.push(opt);
            }
          });

        // Process devices grouped by environment (LOCAL/REMOTE)
        (devices?.data ?? []).forEach((device: any) => {
          const deviceOpt = {
            label: device.name,
            value: device.id,
            deviceType: device.environment,
          };
          // Populate deviceMap for name resolution
          this.deviceMap[device.id] = device.name;
          if (device.environment?.toUpperCase() === 'LOCAL') {
            localDevices.push(deviceOpt);
          } else {
            remoteDevices.push(deviceOpt);
          }
        });

        // Build menu dropdown structure for toolbar dropdown with same hierarchy
        this.executionEngineOptions = [
          // Devices section with Local/Remote sub-sections
          {
            label: 'Devices',
            type: 'devices',
            options: [
              ...(localDevices.length
                ? [{ label: 'Local', options: localDevices }]
                : []),
              ...(remoteDevices.length
                ? [{ label: 'Remote', options: remoteDevices }]
                : []),
            ],
          },
          // Execution Engines section with On Demand/Local/Cloud sub-sections
          {
            label: 'Execution Engines',
            type: 'agents',
            options: [
              {
                label: 'On Demand',
                options: [
                  {
                    label: 'On Demand',
                    icon: 'assets/images/agent/agent-alive.svg',
                    value: 'onDemand',
                    agentType: 'CLUSTER',
                  },
                ],
              },
              ...(localAgents.length
                ? [{ label: 'Local', options: localAgents }]
                : []),
              ...(cloudAgents.length
                ? [{ label: 'Cloud', options: cloudAgents }]
                : []),
            ],
          },
        ];

        // Build nested structure for table dropdown: Devices and Execution Engines as main sections
        this.executionEngineTableOptions = [
          // Devices section with Local/Remote sub-sections - always show even if empty
          {
            label: 'Devices',
            type: 'devices',
            options: [
              ...(localDevices.length
                ? [{ label: 'Local', options: localDevices }]
                : []),
              ...(remoteDevices.length
                ? [{ label: 'Remote', options: remoteDevices }]
                : []),
            ],
          },
          // Execution Engines section with On Demand/Local/Cloud sub-sections
          {
            label: 'Execution Engines',
            type: 'agents',
            options: [
              {
                label: 'On Demand',
                options: [
                  {
                    label: 'On Demand',
                    icon: 'assets/images/agent/agent-alive.svg',
                    value: 'onDemand',
                    agentType: 'CLUSTER',
                  },
                ],
              },
              ...(localAgents.length
                ? [{ label: 'Local', options: localAgents }]
                : []),
              ...(cloudAgents.length
                ? [{ label: 'Cloud', options: cloudAgents }]
                : []),
            ],
          },
        ];

        // script grouping
        const data = scripts?.data?.filter(
          (item: any) => item.type === "SCRIPT",
        );
        const groupsMap = new Map<string, any>();
        const ungroupedRows: any[] = [];

        data?.forEach((item: any) => {
          const row = {
            testCaseId: item.id,
            scriptId: item.scriptId,
            testCaseName: item.name,
            version: item.version,
            increment: item.increment ?? 0,
            versionOptions: item.versions?.map((v: any) => v.version) ?? [],
            // Use agentId from response as the default selected agent if present
            executionEngine: item.agentId ?? null,
            browser: null,
            browserOptions: this.browserOptions.map((o: any) => o.value),
            tcgTestCaseResultId: item.tcgTestCaseResultId ?? null,
            tcgTestCaseResultName: item.tcgTestCaseResultName ?? null,
            active: item.active,
            select: false,
          };

          if (item.automationScriptGroup) {
            const groupId = item.automationScriptGroup.id;
            if (groupsMap.has(groupId)) {
              groupsMap.get(groupId).tableRows.push(row);
              groupsMap.get(groupId).itemCount++;
              groupsMap.get(groupId).testcaseCount++;
            } else {
              groupsMap.set(groupId, {
                id: groupId,
                title: item.automationScriptGroup.name,
                testSuiteName: item.automationScriptGroup.name,
                testcaseCount: 1,
                itemCount: 1,
                testSuiteId: groupId,
                expanded: false,
                isDefault: false,
                tableColumns: this.getDefaultTableColumns(),
                tableRows: [row],
              });
            }
          } else {
            ungroupedRows.push(row);
          }
        });

        const accordionData = [...groupsMap.values()];

        // Scripts without a group, default section
        if (ungroupedRows.length > 0) {
          accordionData.push({
            id: "default-test-cases",
            title: "Test Scripts",
            testSuiteName: "Test Scripts",
            testcaseCount: ungroupedRows.length,
            itemCount: ungroupedRows.length,
            testSuiteId: "default-test-cases",
            expanded: false,
            isDefault: true,
            tableColumns: this.getDefaultTableColumns(),
            tableRows: ungroupedRows,
          });
        }

        this.configs.accordionData = accordionData;
        this.initializeDropdownOptions();
        if (plan?.data) {
          this.applyExistingPlan(plan.data);
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private applyExistingPlan(planData: any): void {
    // Pre-fill test plan name
    this.dynamicForm.get('testPlanName')?.setValue(planData.name ?? '');

    // Build a fast lookup: testCaseId -> saved script data
    const scriptMap = new Map<string, any>();
    (planData.testScripts ?? []).forEach((s: any) => {
      scriptMap.set(s.id, s);
    });

    // Walk every accordion row, pre-select those that exist in the saved plan
    this.configs.accordionData.forEach((suite: any) => {
      suite.tableRows.forEach((row: any) => {
        const script = scriptMap.get(row.testCaseId);
        if (script) {
          row.select = true;
          // null agentId from the API means On Demand was chosen
          row.executionEngine = script.agentId ?? 'onDemand';
          row.browser = script.browser ?? null;
          row.version = script.version ?? row.version;
          this.selectedTestCases.push({
            testCaseId: row.testCaseId,
            scriptId: row.scriptId,
            testCaseName: row.testCaseName,
            version: row.version,
            increment: row.increment ?? 0,
            tcgTestCaseResultId: row.tcgTestCaseResultId ?? null,
            executionEngine: row.executionEngine,
            browser: row.browser,
          });
        }
      });
      // Trigger ZorroTable ngOnChanges to re-render checkboxes & dropdowns
      suite.tableRows = [...suite.tableRows];
    });

    this.selectedTestCaseCount = this.selectedTestCases.length;
  }

  private getDefaultTableColumns(): any[] {
    return [
      {
        display_name: "",
        key: "select",
        headerStyles: { width: "5%" },
        _config: {
          type: "checkbox",
          displayInColumn: true,
        },
      },
      {
        display_name: "Test Case Name",
        key: "testCaseName",
        headerStyles: { width: "40%" },
        cellStyles: { "word-break": "break-word", "white-space": "normal", "max-width": "0", "overflow-wrap": "break-word" },
      },
      {
        display_name: "Version",
        key: "version",
        headerStyles: { width: "20%" },
        _config: {
          type: "multi-select",
          displayInColumn: false,
          options: [],
          placeholder: "Select version",
        },
      },
      {
        display_name: "Execution Engine/ Device",
        key: "executionEngine",
        headerStyles: { width: "25%" },
        _config: {
          type: "menu_dropdown",
          targetKey: "executionEngine",
          componentInputs: {
            fields: [],
          },
          placeholder: "Select",
        },
      },
      {
        display_name: "Browser",
        key: "browser",
        headerStyles: { width: "10%" },
        _config: {
          type: "browser-select",
          optionsKey: "browserOptions",
          imageMap: {
            Chrome: "assets/images/virtusa-icons/chrome.svg",
            Firefox: "assets/images/virtusa-icons/firefox.svg",
            Edge: "assets/images/virtusa-icons/edge.svg",
          },
        },
      },
    ];
  }
}
